import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Select,
    MenuItem,
    SelectChangeEvent,
    TextField,
    Button,
} from "@mui/material";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SearchBar from "../../components/SearchBar";

const ManagePayments = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [tabIndex, setTabIndex] = useState(0);
    const [earningsSubTab, setEarningsSubTab] = useState(0);
    const tabLabels = ["Payments", "Earnings"];
    const earningsTabs = ["Vendor Earnings", "Service Provider Earnings"];


    // ------------------ Payments Dummy Data ------------------
    const paymentData = [
        {
            orderId: "#Order_1001",
            customer: { name: "John Doe", email: "john@example.com", phone: "+91 9876543210" },
            price: "AED 120.00",
            orderDate: "01 Oct 2025",
        },
        {
            orderId: "#Order_1002",
            customer: { name: "Alice Smith", email: "alice@example.com", phone: "+91 9123456789" },
            price: "AED 80.00",
            orderDate: "03 Oct 2025",
        },
    ];

    // ------------------ Earnings Dummy Data ------------------
    const vendorEarnings = [
        {
            orderId: "#1234",
            vendorId: "#V1234",
            customer: { name: "John Doe", email: "john@example.com" },
            orderAmount: "AED 100.00",
            shippingCharge: "AED 10.00",
            commission: "AED 5.00",
            paymentMethod: "Card",
            orderDate: "2025-08-03",
            status: "Paid",
        },
        {
            orderId: "#5678",
            vendorId: "#V1234",
            customer: { name: "Jane Smith", email: "jane@example.com" },
            orderAmount: "AED 80.00",
            shippingCharge: "AED 5.00",
            commission: "AED 4.00",
            paymentMethod: "Card",
            orderDate: "2025-08-01",
            status: "Paid",
        },
        {
            orderId: "#9012",
            vendorId: "#V1234",
            customer: { name: "Robert Fox", email: "robert@example.com" },
            orderAmount: "AED 120.00",
            shippingCharge: "AED 8.00",
            commission: "AED 6.00",
            paymentMethod: "Card",
            orderDate: "2025-08-05",
            status: "Pending",
        },
    ];

    const serviceProviderEarnings = [
        {
            orderId: "#9101",
            providerId: "#S1234",
            customer: { name: "David Miller", email: "david@example.com" },
            orderAmount: "AED 150.00",
            shippingCharge: "AED 15.00",
            commission: "AED 7.00",
            paymentMethod: "UPI",
            orderDate: "2025-08-04",
            status: "Paid",
        },
        {
            orderId: "#1011",
            providerId: "#S1234",
            customer: { name: "Emily Davis", email: "emily@example.com" },
            orderAmount: "AED 180.00",
            shippingCharge: "AED 20.00",
            commission: "AED 9.00",
            paymentMethod: "UPI",
            orderDate: "2025-08-06",
            status: "Pending",
        },
    ];

    const [status, setStatus] = useState("default");

    // ------------------ Payout Statistics Calculation ------------------
    const allEarnings = [...vendorEarnings, ...serviceProviderEarnings];

    const totalPayoutsAmount = allEarnings
        .filter(item => item.status === "Paid")
        .reduce((sum, item) => sum + parseFloat(item.orderAmount.replace('AED ', '')), 0);

    const completedCount = allEarnings.filter(item => item.status === "Paid").length;
    const pendingCount = allEarnings.filter(item => item.status === "Pending").length;

    // For "Active Schedules", since there's no specific table or data yet, 
    // we'll add a dummy count or a dummy array to represent it.
    const activeSchedulesCount = 2; // Dummy count for demonstration

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleEarningsSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setEarningsSubTab(newValue);
    };

    return (
        <>
            <div className="main_title">
                <h1>Manage Payments</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Payments
                </p>
            </div>

            <div className="stats_boxes gap_m">

                {/* TOTAL PAYOUTS */}
                <a className="cards">
                    <div className="data_top">
                        <h2>
                            AED {totalPayoutsAmount.toFixed(2)}
                            <p>Total Payment</p>
                        </h2>
                        <figure className="blue">
                            <AttachMoneyOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>


            </div>

            {/* Top-Level Tabs */}
            <Box className="custom_tabs1 mt_20">
                <Tabs value={tabIndex} onChange={handleTabChange} className="custom_tabs_links">
                    {tabLabels.map((label, index) => (
                        <Tab key={index} label={label} />
                    ))}
                </Tabs>
            </Box>
            <div className="table_flex">
                <div className="lt">

                    <div className="cards">
                        {tabIndex !== 2 && (

                            <div className="table_header">
                                <Box className="search_box">
                                    <SearchBar
                                        searchTerm={searchTerm}
                                        setDebouncedSearchTerm={setSearchTerm}
                                        placeholder="Search by order ID or customer..."
                                    />
                                </Box>
                            </div>
                        )}

                        {/* ---------------- Payments Tab ---------------- */}
                        {tabIndex === 0 && (
                            <TableContainer className="table_container">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order ID</TableCell>
                                            <TableCell>Customer Detail</TableCell>
                                            <TableCell>Price</TableCell>
                                            <TableCell>Order Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentData.map((payment, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{payment.orderId}</TableCell>
                                                <TableCell>
                                                    <div className="user_block">
                                                        <figure>
                                                            <img src="/static/images/user_placeholder.png" alt="Vendor" />
                                                        </figure>
                                                        <p>
                                                            <b>{payment.customer.name}</b>
                                                            {payment.customer.email} <br />
                                                            {payment.customer.phone}                                                  </p>
                                                    </div>


                                                </TableCell>
                                                <TableCell>{payment.price}</TableCell>
                                                <TableCell>{payment.orderDate}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* ---------------- Earnings Tab ---------------- */}
                        {tabIndex === 1 && (
                            <>
                                <Box className="custom_tabs1" sx={{ mt: 2 }}>
                                    <Tabs
                                        value={earningsSubTab}
                                        onChange={handleEarningsSubTabChange}
                                        className="custom_tabs_links small_tabs"
                                    >
                                        {earningsTabs.map((label, i) => (
                                            <Tab key={i} label={label} />
                                        ))}
                                    </Tabs>
                                </Box>

                                {/* Vendor Earnings */}
                                {earningsSubTab === 0 && (
                                    <TableContainer className="table_container" sx={{ mt: 2 }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Order ID</TableCell>
                                                    <TableCell>Vendor ID</TableCell>
                                                    <TableCell>Customer Detail</TableCell>
                                                    <TableCell>Order Amount</TableCell>
                                                    <TableCell>Shipping Charge</TableCell>
                                                    <TableCell>Commission</TableCell>
                                                    <TableCell>Payment Method</TableCell>
                                                    <TableCell>Order Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {vendorEarnings.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{item.orderId}</TableCell>
                                                        <TableCell>{item.vendorId}</TableCell>
                                                        <TableCell>
                                                            <div className="user_block">
                                                                <figure>
                                                                    <img src="/static/images/user_placeholder.png" alt="Vendor" />
                                                                </figure>
                                                                <p>
                                                                    <b>{item.customer.name}</b>
                                                                    {item.customer.email}                                                     </p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>{item.orderAmount}</TableCell>
                                                        <TableCell>{item.shippingCharge}</TableCell>
                                                        <TableCell sx={{ color: "green" }}>{item.commission}</TableCell>
                                                        <TableCell>{item.paymentMethod}</TableCell>
                                                        <TableCell>{item.orderDate}</TableCell>
                                                        <TableCell>{item.status}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Service Provider Earnings */}
                                {earningsSubTab === 1 && (
                                    <TableContainer className="table_container" sx={{ mt: 2 }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Order ID</TableCell>
                                                    <TableCell>Service Provider ID</TableCell>
                                                    <TableCell>Customer Detail</TableCell>
                                                    <TableCell>Order Amount</TableCell>
                                                    <TableCell>Shipping Charge</TableCell>
                                                    <TableCell>Commission</TableCell>
                                                    <TableCell>Payment Method</TableCell>
                                                    <TableCell>Order Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {serviceProviderEarnings.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{item.orderId}</TableCell>
                                                        <TableCell>{item.providerId}</TableCell>
                                                        <TableCell>
                                                            <div className="user_block">
                                                                <figure>
                                                                    <img src="/static/images/user_placeholder.png" alt="Vendor" />
                                                                </figure>
                                                                <p>
                                                                    <b>{item.customer.name}</b>
                                                                    {item.customer.email}                                                        </p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>{item.orderAmount}</TableCell>
                                                        <TableCell>{item.shippingCharge}</TableCell>
                                                        <TableCell sx={{ color: "green" }}>{item.commission}</TableCell>
                                                        <TableCell>{item.paymentMethod}</TableCell>
                                                        <TableCell>{item.orderDate}</TableCell>
                                                        <TableCell>{item.status}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                    </div >
                </div>
                <div className="rt">
                    <div className="ai_panel">
                        <h4>AI Insights</h4>

                        <div className="ai_alert warning">
                            <span className="icon">⚠️</span>
                            <p>
                                <b>7 SKUs</b> need immediate attention
                            </p>
                        </div>

                        <div className="ai_alert danger">
                            <span className="icon">⛔</span>
                            <p>
                                <b>3 SKUs</b> have zero sales in 30 days
                            </p>
                        </div>

                        <small className="ai_update">
                            Next updated ETA: <b>May 5, 2024</b>
                        </small>

                        <div className="ai_sku_list">
                            <div className="ai_sku_row">
                                <div className="ai_sku_info">
                                    <h5>#PRD_002</h5>
                                    <p>Likely to go out of stock by May</p>
                                </div>
                                <button className="ai_action_btn">Actions ▾</button>
                            </div>

                            <div className="ai_sku_row">
                                <div className="ai_sku_info">
                                    <h5>#PRD_010</h5>
                                    <p>Likely to go out of stock by May</p>
                                </div>
                                <button className="ai_action_btn">Actions ▾</button>
                            </div>
                        </div>

                        {/* <div className="ai_create">
                                    + Create AI Insights
                                </div> */}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ManagePayments;
